import { Container } from './Container'
import { Header } from './Header'
import { MessageList } from './MessageList'
import { Composer } from './Composer/Composer'
import { useMemo, useEffect, useRef } from 'react'
import type {
  BlockMessage,
  Feedback,
  FileType,
  IntegrationMessage,
  Renderers,
  RichBlockMessage,
  WebchatError,
} from '../types'
import { enrichMessage } from '../utils'
import { useWebchatStore } from '../stores'
import { LatestWebchatConfig } from '@bpinternal/shared'
import { User } from '@botpress/webchat-client'
import { StylesheetProvider } from '../providers'
import { ErrorState } from './Error'
import { Loader } from './Loader'

export type Props = {
  className?: string
  renderers?: Partial<Renderers>
  configuration: LatestWebchatConfig
  messages: BlockMessage[]
  participants: User[]
  connected?: boolean
  disableComposer?: boolean
  isReadOnly?: boolean
  isLoading?: boolean
  isTyping?: boolean
  lastTypingHeartbeat?: Date | null
  allowFileUpload?: boolean
  clientId?: string
  closeWindow?: () => void
  restartConversation?: () => void
  user?: { id: string } & Record<string, unknown>
  sendMessage?: (payload: IntegrationMessage['payload']) => Promise<void>
  uploadFile?: (file: File) => Promise<{ fileUrl: string; name: string; type: FileType }>
  addMessageFeedback?: (messageId: string, feedback: Feedback) => void
  triggerNetworkError?: (errorType?: 'network' | 'timeout' | 'server', delay?: number) => Promise<never>
  error?: WebchatError
  soundOn?: boolean
  toggleSound?: () => void
  disableSendButton?: boolean
  storageKey?: string
}

export const Chat = ({
  connected,
  configuration,
  isTyping,
  messages,
  user,
  isLoading,
  renderers,
  isReadOnly,
  disableComposer,
  allowFileUpload,
  error,
  participants,
  closeWindow,
  sendMessage,
  uploadFile,
  addMessageFeedback,
  restartConversation,
  clientId,
  storageKey,
  soundOn,
  toggleSound,
  disableSendButton,
  ...props
}: Props) => {
  const {
    color,
    fontFamily,
    radius,
    themeMode,
    variant,
    headerVariant,
    botAvatar,
    botDescription,
    botName,
    composerPlaceholder,
    footer,
    feedbackEnabled,
    additionalStylesheetUrl,
  } = configuration

  const themeProps = { color, fontFamily, radius, themeMode, variant, headerVariant, additionalStylesheetUrl }

  if (!feedbackEnabled) {
    addMessageFeedback = undefined
  }

  const enrichedMessages: RichBlockMessage[] = useMemo(() => {
    return user ? enrichMessage(messages, participants, user.id, botName, botAvatar) : []
  }, [botAvatar, botName, messages, user, participants])

  const portalRef = useRef<HTMLDivElement>(null)
  const setPortalContainer = useWebchatStore((s) => s.setPortalContainer)

  useEffect(() => {
    setPortalContainer(portalRef.current)
    return () => {
      setPortalContainer(null)
    }
  }, [setPortalContainer])

  return (
    <>
      <StylesheetProvider {...themeProps} />
      {/* Portal container for dropdowns - positioned to allow overflow */}
      <div
        ref={portalRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 10,
        }}
      />
      <Container
        connected={connected}
        allowFileUpload={allowFileUpload}
        uploadFile={uploadFile}
        storageKey={storageKey}
        {...props}
      >
        {error ? (
          <ErrorState error={error} restartConversation={restartConversation} closeWindow={closeWindow} />
        ) : (
          <>
            <Header
              closeWindow={closeWindow}
              configuration={configuration}
              restartConversation={restartConversation}
              clientId={clientId}
              soundOn={soundOn}
              toggleSound={toggleSound}
            />
            {isLoading ? (
              <Loader />
            ) : (
              <MessageList
                {...{ botAvatar, botDescription, botName, renderers, isTyping, sendMessage, addMessageFeedback }}
                messages={enrichedMessages}
              />
            )}
            <Composer
              {...{
                sendMessage,
                uploadFile,
                connected,
                isReadOnly,
                disableComposer,
                footer,
                composerPlaceholder,
                allowFileUpload,
                disableSendButton,
                storageKey,
              }}
            />
          </>
        )}
      </Container>
    </>
  )
}
